import { faAlarmExclamation } from '@fortawesome/pro-light-svg-icons';
import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppointmentListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import ListPetInfo from 'components/Bookings2/UI/ListPetInfo/ListPetInfo';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { FunctionComponent, MouseEvent, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import NotesTableRow from './NotesTableRow';
import PaidIcon from './PaidIcon';
import DepositPendingIcon from './DepositPendingIcon';
import PetParent from './PetParent';
import styles from './RequestedTableRow.module.scss';
import Service from './Service';
import Button from 'components/UI/V3/Button/Button';
import { changeBookingStatusThunk, NewBookingsStatus } from '@spike/new-bookings-action';
import { useDispatch, useSelector } from 'react-redux';
import { status as AppointmentStatus } from '@spike/appointment-model';
import DeclineConfirmation from 'components/Notifications/UI/DeclineConfirmation';
import useNonInitialEffect from '@versiondos/hooks';
import { RootState } from 'store';
import BoardingCheckMoment from './BoardingCheckMoment';
import { faMoon } from '@fortawesome/pro-solid-svg-icons';
import { getServiceCategoryByTabId } from './tabs';
import Staff from './Staff';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface RequestedTableRowProps {
    app: AppointmentListItem;
    selectedTabId: string;
    isLast?: boolean;
    scrollerContainerId: string;
    className?: string;
    onCheckout?: () => void;
    onShowOrder?: () => void;
    onShowClient?: () => void;
    onShowPet?: () => void;
    onStatusChanged?: () => void;
    onEdit?: () => void;
}

export const RequestedTableRow: FunctionComponent<RequestedTableRowProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const timezone = useTimeZone();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const allowActions = isUserLoggedInMarketplace(props.app.marketplaceId);

    const serviceCategory = getServiceCategoryByTabId(props.selectedTabId);

    const hasNotes = props.app.notes.trim().length > 0;

    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const [loading, setLoading] = useState({ decline: false, accept: false });
    const [showDeclineDialog, setShowDeclineDialog] = useState(false);

    const checkoutOption = {
        id: 'checkout',
        label: 'Checkout',
        icon: faMoneyBill,
        onClick: () => props.onCheckout?.()
    };

    const options = [checkoutOption];

    useNonInitialEffect(() => {
        if (loading.decline || loading.accept) {
            switch (newBookingsStatus) {
                case NewBookingsStatus.ChangeStatusSuccess:
                    setLoading(prev => ({ ...prev, decline: false, accept: false }));
                    props.onStatusChanged?.();
                    break;
                case NewBookingsStatus.Error:
                    setLoading(prev => ({ ...prev, decline: false, accept: false }));
            }
        }
    }, [newBookingsStatus]);

    const clickHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        props.onShowOrder?.();
    };

    const declineHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setShowDeclineDialog(true);
    };

    const confirmDeclineHandler = (sendNotification: boolean) => {
        setLoading(prev => ({ ...prev, decline: true }));
        changeBookingStatus(AppointmentStatus.DECLINED, sendNotification);
    };

    const acceptHandler = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        setLoading(prev => ({ ...prev, accept: true }));
        changeBookingStatus(AppointmentStatus.BOOKED);
    };

    const editHandler = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        props.onEdit?.();
    };

    const changeBookingStatus = async (status: number, sendNotification = false) => {
        dispatch(changeBookingStatusThunk(apiClientWrapper, props.app.bookingId, status, false, sendNotification));
    };

    const petInfo = (
        <ListPetInfo
            orderId={props.app.orderId}
            pet={props.app.pet}
            onClick={allowActions ? props.onShowPet : undefined}
        />
    );

    const service = (
        <Service
            service={props.app.service}
            petId={props.app.pet.id}
            marketplaceId={props.app.marketplaceId}
        />
    );

    const alarm = props.app.pendingDropOff ? (
        <FontAwesomeIcon
            icon={faAlarmExclamation}
            className={styles.alarm}
        />
    ) : null;

    const boardingDateTime = (
        <div className={styles.boardingDateTimeContainer}>
            <div className={styles.boardingDateTimeRow}>
                <div className={styles.boardingDateTimeLeft}>
                    <strong>Check In</strong>
                </div>
                <div className={styles.boardingDateTimeRight}>
                    {props.app.startAt.clone().format('MMM D, YYYY h:mma')}
                </div>
            </div>
            <div className={styles.boardingDateTimeRow}>
                <div className={styles.boardingDateTimeLeft}>
                    <strong>Check Out</strong>
                </div>
                <div className={styles.boardingDateTimeRight}>
                    {props.app.endAt.clone().format('MMM D, YYYY h:mma')}
                </div>
            </div>
        </div>
    );

    const otherDateTime = (
        <>
            {props.app.startAt.format('MMM DD, YYYY [at] h:mm a')}
            {alarm}
        </>
    );

    const checkIn = (
        <BoardingCheckMoment
            moment={props.app.startAt}
            pendingDropOff={props.app.pendingDropOff}
        />
    );
    const checkOut = <BoardingCheckMoment moment={props.app.endAt} />;
    const nights = <span>{props.app.nights} nights</span>;

    const mobileBoardingDateTime = (
        <div className={styles.mobileChecksContainer}>
            <div className={styles.mobileCheckContainer}>
                <span className={styles.mobileCheckTitle}>Check In</span>
                {checkIn}
            </div>
            <div className={styles.mobileCheckContainer}>
                <span className={styles.mobileCheckTitle}>Check Out</span>
                {checkOut}
            </div>
            <div className={styles.mobileNightsContainer}>
                <FontAwesomeIcon icon={faMoon} />
                {nights}
            </div>
        </div>
    );

    const notMobileRow = (
        <>
            <TableRow
                className={clsx(styles.row, { [styles.withoutBorder]: hasNotes }, props.className)}
                onClick={clickHandler}
            >
                <TableCell className={styles.clientNameCell}>
                    <PetParent
                        customer={props.app.customer}
                        marketplaceId={props.app.marketplaceId}
                        showOnly={!allowActions}
                        showTagsIcon={!isMobile}
                        onClick={props.onShowClient}
                    />
                </TableCell>
                <TableCell>{petInfo}</TableCell>
                <TableCell>{service}</TableCell>
                {serviceCategory === 'grooming' && (
                    <TableCell className={styles.staffCell}>
                        <Staff
                            appointmentId={props.app.appointmentId}
                            appointmentStatusId={props.app.status.id}
                            member={props.app.staff}
                        />
                    </TableCell>
                )}
                {serviceCategory === 'boarding' ? (
                    <>
                        <TableCell className={styles.cell}>{checkIn}</TableCell>
                        <TableCell className={styles.cell}>{checkOut}</TableCell>
                        <TableCell className={clsx(styles.cell, styles.nighCell)}>{nights}</TableCell>
                    </>
                ) : (
                    <TableCell>
                        {props.app.service.businessArea === 'boarding' ? boardingDateTime : otherDateTime}
                    </TableCell>
                )}
                <TableCell className={styles.priceCell}>
                    ${Number(props.app.total).toFixed(2)}
                    <DepositPendingIcon show={props.app.bookingDepositPending} />
                    <PaidIcon paid={props.app.paid} />
                </TableCell>
                <TableCell
                    isActions
                    onClick={event => event.stopPropagation()}
                >
                    {allowActions && (
                        <>
                            <Button
                                label="Decline"
                                size="small"
                                variant="danger"
                                onClick={declineHandler}
                                isLoading={loading.decline}
                                disabled={loading.accept}
                            />
                            <Button
                                label="Edit"
                                size="small"
                                variant="outlined"
                                onClick={editHandler}
                                disabled={loading.decline || loading.accept}
                            />
                            <Button
                                label="Accept"
                                size="small"
                                variant="green"
                                onClick={acceptHandler}
                                isLoading={loading.accept}
                                disabled={loading.decline}
                            />
                        </>
                    )}
                </TableCell>
            </TableRow>
            {hasNotes && (
                <NotesTableRow
                    notes={props.app.notes}
                    colSpan={8}
                />
            )}
            {showDeclineDialog && (
                <DeclineConfirmation
                    loading={loading.decline}
                    onCancel={() => setShowDeclineDialog(false)}
                    onConfirm={sendNotification => confirmDeclineHandler(sendNotification)}
                />
            )}
        </>
    );

    const mobileRow = (
        <>
            <TableRow
                className={clsx(styles.mobileRow, props.className)}
                onClick={clickHandler}
            >
                <TableCell className={styles.mobileCell}>
                    <div className={styles.mobileHeader}>
                        <div className={styles.mobilePetContainer}>{petInfo}</div>
                        {props.app.service.businessArea !== 'boarding' && (
                            <div className={styles.mobileDateTimeContainer}>
                                <span className={styles.mobileDateTime}>
                                    {props.app.startAt.format('MMM DD, h:mm a')}
                                </span>
                                {alarm}
                            </div>
                        )}
                    </div>
                    <div className={styles.mobileBody}>
                        {serviceCategory === 'grooming' ? (
                            <div className={styles.mobileServiceStaffRow}>
                                {service}{' '}
                                <Staff
                                    appointmentId={props.app.appointmentId}
                                    appointmentStatusId={props.app.status.id}
                                    member={props.app.staff}
                                    scrollerContainerId={props.scrollerContainerId}
                                />
                            </div>
                        ) : (
                            service
                        )}
                        {props.app.service.businessArea === 'boarding' && mobileBoardingDateTime}
                    </div>
                    <div className={styles.mobileFooter}>
                        {hasNotes && (
                            <NotesTableRow
                                notes={props.app.notes}
                                colSpan={8}
                            />
                        )}
                        {allowActions && (
                            <div className={styles.mobileButtonsContainer}>
                                <Button
                                    label="Decline"
                                    variant="danger"
                                    fullWidth={true}
                                    onClick={declineHandler}
                                    isLoading={loading.decline}
                                    disabled={loading.accept}
                                />
                                <Button
                                    label="Edit"
                                    variant="outlined"
                                    fullWidth={true}
                                    onClick={editHandler}
                                    disabled={loading.decline || loading.accept}
                                />
                                <Button
                                    label="Accept"
                                    variant="green"
                                    fullWidth={true}
                                    onClick={acceptHandler}
                                    isLoading={loading.accept}
                                    disabled={loading.decline}
                                />
                            </div>
                        )}
                    </div>
                </TableCell>
            </TableRow>
        </>
    );

    return isMobile ? mobileRow : notMobileRow;
};

export default RequestedTableRow;
